import { useState, useCallback, useEffect } from 'react';
import { trpc } from '../api/trpc';

// AWS connector install wizard. Spec topics/connectors/aws-provisioning.md §8.1.
//
// Simpler than the Microsoft wizard: no admin-consent OAuth dance, no
// permission picker — the customer chooses a tier preset, we generate a
// CloudFormation Quick Create URL, they launch the stack, paste the role ARN
// back, we test the connection. Spec §4.2 end-to-end flow.

type WizardState =
  | 'tier_selection'
  | 'launch_stack'
  | 'awaiting_role_arn'
  | 'testing'
  | 'success'
  | 'failed';

type Tier = 'full-read' | 'posture-and-compliance';

interface Props {
  onComplete: () => void;
  onStateChange?: (state: string) => void;
}

const STEPS = ['Choose tier', 'Launch CloudFormation', 'Verify role', 'Done'] as const;

function stepIndexForState(state: WizardState): number {
  switch (state) {
    case 'tier_selection': return 0;
    case 'launch_stack': return 1;
    case 'awaiting_role_arn': return 2;
    case 'testing':
    case 'success':
    case 'failed': return 3;
  }
}

/** True when dismissing would lose progress. */
export function isAwsProvisioningInProgress(state: WizardState): boolean {
  return state === 'launch_stack' || state === 'awaiting_role_arn' || state === 'testing';
}

interface TierOption {
  id: Tier;
  label: string;
  summary: string;
  details: string;
  /** Advanced tiers are hidden behind the "Advanced settings" disclosure. */
  advanced: boolean;
}

const TIER_OPTIONS: TierOption[] = [
  {
    id: 'full-read',
    label: 'Standard access',
    summary: 'All the read-only access Cade uses for posture assessment and compliance reviews.',
    details: 'Cade keeps up with new checks automatically, with no need to re-run this install.',
    advanced: false,
  },
  {
    id: 'posture-and-compliance',
    label: 'Posture + Compliance',
    summary: 'A narrower read-only grant: posture checks plus Inspector, Macie, Access Analyzer, and CloudWatch metric filters.',
    details: 'For security teams that require a minimum permission set. You may need to re-run this install when Cade adds new checks.',
    advanced: true,
  },
];

// ─── Small UI helpers (mirrors patterns from MicrosoftAutomatedInstall) ─────

function Spinner({ size = 28 }: { size?: number }) {
  return (
    <div style={{
      width: size, height: size, border: '3px solid var(--color-border)',
      borderTopColor: 'var(--color-accent)', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite', margin: '0 auto',
    }} />
  );
}

function CopyButton({ text, label = 'Copy', disabled }: { text: string; label?: string; disabled?: boolean }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (!text) return;
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      type="button"
      className="btn-secondary"
      onClick={handleCopy}
      disabled={disabled || !text}
      style={{ fontSize: 12, whiteSpace: 'nowrap' }}
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, gap: 12 }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{
        color: 'var(--color-text-primary)',
        fontFamily: mono ? 'var(--font-mono)' : undefined,
        fontSize: mono ? 11 : undefined,
        textAlign: 'right',
        wordBreak: 'break-all',
      }}>{value}</span>
    </div>
  );
}

function TierCard({ opt, selected, onSelect }: {
  opt: TierOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 12,
        padding: 12,
        border: `1px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 6,
        cursor: 'pointer',
        background: selected ? 'var(--color-accent-soft, #1d4ed811)' : 'transparent',
      }}
    >
      <input
        type="radio"
        name="aws-tier"
        value={opt.id}
        checked={selected}
        onChange={onSelect}
        style={{ marginTop: 4 }}
      />
      <div>
        <div style={{ fontWeight: 600 }}>{opt.label}</div>
        <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{opt.summary}</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>{opt.details}</div>
      </div>
    </label>
  );
}

function ProvisioningStep({ label, done, active }: { label: string; done: boolean; active: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
      {done ? (
        <span style={{ color: 'var(--sev-success)', fontSize: 16, width: 20, textAlign: 'center' }}>{'✓'}</span>
      ) : active ? (
        <Spinner size={16} />
      ) : (
        <span style={{ color: 'var(--color-border)', fontSize: 16, width: 20, textAlign: 'center' }}>{'○'}</span>
      )}
      <span style={{ fontSize: 13, color: done ? 'var(--color-text-secondary)' : active ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
        {label}
      </span>
    </div>
  );
}

function StepBar({ current }: { current: number }) {
  return (
    <ol className="wizard-steps" style={{ display: 'flex', listStyle: 'none', padding: 0, margin: '0 0 24px 0', gap: 8 }}>
      {STEPS.map((label, i) => (
        <li
          key={label}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 6,
            background: i <= current ? 'var(--color-accent-soft, #1d4ed822)' : 'var(--color-bg-elevated)',
            color: i <= current ? 'var(--color-accent)' : 'var(--color-text-muted)',
            fontSize: 12,
            textAlign: 'center',
            border: '1px solid var(--color-border)',
          }}
        >
          <span style={{ fontWeight: 600, marginRight: 4 }}>{i + 1}.</span>
          {label}
        </li>
      ))}
    </ol>
  );
}

// Animated checklist labels for the testing phase. Each reflects a real
// step the connector performs in `captureRoleArn` (verify trust → assume →
// describe account → list orgs → enumerate regions). We don't have streaming
// progress from the backend yet; advance by elapsed time so the user sees
// motion instead of a single-line ellipsis.
const TESTING_STEPS = [
  'Verifying role trust policy',
  'Assuming role via web identity',
  'Describing AWS account',
  'Detecting active regions',
  'Saving connection',
] as const;

export function AwsAutomatedInstall({ onComplete, onStateChange }: Props) {
  const [state, setState] = useState<WizardState>('tier_selection');
  const [tier, setTier] = useState<Tier>('full-read');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [installUrl, setInstallUrl] = useState<{
    cloudFormationUrl: string;
    templateUrl: string;
    cadeTenantId: string;
    externalId: string;
    suggestedStackName: string;
    templateFileName: string;
  } | null>(null);
  const [roleArn, setRoleArn] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [testingStep, setTestingStep] = useState(0);
  const [successInfo, setSuccessInfo] = useState<{
    awsAccountId: string;
    accountAlias?: string;
    isManagementAccount: boolean;
    regions: string[];
  } | null>(null);

  // Keep parent informed so it knows whether to allow modal-dismiss.
  const transition = useCallback((next: WizardState) => {
    setState(next);
    onStateChange?.(next);
  }, [onStateChange]);

  // Animated testing-step ticker. Mirrors Microsoft's provisioning checklist;
  // advances every ~600ms but caps before the last step so the final tick
  // happens when the backend resolves (success/failed).
  useEffect(() => {
    if (state !== 'testing') {
      setTestingStep(0);
      return;
    }
    const t = setInterval(() => {
      setTestingStep((s) => Math.min(s + 1, TESTING_STEPS.length - 1));
    }, 600);
    return () => clearInterval(t);
  }, [state]);

  const generateMut = trpc.connectors.provisioning.aws.generateInstallUrl.useMutation();
  const captureMut = trpc.connectors.provisioning.aws.captureRoleArn.useMutation();

  const launchStack = useCallback(async () => {
    setError('');
    try {
      const r = await generateMut.mutateAsync({ tier });
      setInstallUrl(r);
      transition('launch_stack');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate install URL');
    }
  }, [generateMut, tier, transition]);

  const captureArn = useCallback(async () => {
    setError('');
    setErrorCode(null);

    const trimmedArn = roleArn.trim();
    // Spec §5.2 req 759 — the role ARN is the only input; the account ID is
    // derived from it server-side.
    if (!/^arn:aws:iam::\d{12}:role\/.+/.test(trimmedArn)) {
      setError('Paste the role ARN from the CloudFormation stack Outputs tab, e.g. arn:aws:iam::123456789012:role/CadeSecurityAuditRole.');
      return;
    }

    if (!installUrl) {
      setError('Install URL is missing — please go back and re-launch.');
      return;
    }

    transition('testing');
    try {
      const r = await captureMut.mutateAsync({
        tier,
        roleArn: trimmedArn,
        externalId: installUrl.externalId,
      });
      setSuccessInfo({
        awsAccountId: r.awsAccountId,
        ...(r.accountAlias ? { accountAlias: r.accountAlias } : {}),
        isManagementAccount: r.isManagementAccount,
        regions: r.regions,
      });
      transition('success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Test connection failed';
      // tRPC wraps ValidationError; extract the AWS code if shape includes it.
      const data = (err as { data?: { awsErrorCode?: string } } | null)?.data;
      setErrorCode(data?.awsErrorCode ?? null);
      setError(message);
      transition('failed');
    }
  }, [captureMut, installUrl, roleArn, tier, transition]);

  // ── Render per state ──────────────────────────────────────────────────────

  if (state === 'success' && successInfo) {
    return (
      <div>
        <StepBar current={3} />
        <div style={{ textAlign: 'center', padding: '24px 0 16px 0' }}>
          <div style={{ fontSize: 32, color: 'var(--sev-success)' }}>✓</div>
          <h4 style={{ margin: '12px 0 4px 0' }}>Connected to AWS</h4>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
            Cade can now run posture assessments and compliance reviews on this account.
          </div>
        </div>
        <div style={{
          background: 'var(--color-bg-elevated)',
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          padding: 12,
          marginBottom: 16,
        }}>
          <InfoRow label="Account ID" value={successInfo.awsAccountId} mono />
          {successInfo.accountAlias && (
            <InfoRow label="Account alias" value={successInfo.accountAlias} />
          )}
          <InfoRow label="Tier" value={tier} />
          <InfoRow
            label="Regions discovered"
            value={successInfo.regions.length === 0 ? '—' : `${successInfo.regions.length}`}
          />
          {successInfo.isManagementAccount && (
            <InfoRow label="Account type" value="Organizations management" />
          )}
        </div>
        {successInfo.isManagementAccount && (
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: -4, marginBottom: 16 }}>
            Member-account StackSet rollout is coming soon — for now, member accounts need to be
            connected individually.
          </p>
        )}
        <div className="modal-actions">
          <button type="button" className="btn-primary" onClick={onComplete}>Done</button>
        </div>
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <div>
        <StepBar current={3} />
        <div style={{ padding: '16px 0' }}>
          <h4 style={{ margin: '0 0 12px 0', color: 'var(--sev-critical)' }}>
            Couldn't verify the role
          </h4>
          {errorCode && (
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              AWS error code: <code>{errorCode}</code>
            </div>
          )}
          <p style={{ whiteSpace: 'pre-wrap' }}>{error}</p>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => transition('awaiting_role_arn')}>
            Back
          </button>
          <button type="button" className="btn-primary" onClick={() => { void captureArn(); }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (state === 'testing') {
    return (
      <div>
        <StepBar current={3} />
        <div style={{ padding: '24px 0' }}>
          <h4 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>Verifying connection</h4>
          <div style={{
            maxWidth: 320,
            margin: '0 auto',
            background: 'var(--color-bg-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            padding: 16,
          }}>
            {TESTING_STEPS.map((label, i) => (
              <ProvisioningStep
                key={label}
                label={label}
                done={i < testingStep}
                active={i === testingStep}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (state === 'awaiting_role_arn') {
    return (
      <div>
        <StepBar current={2} />
        <h4 style={{ margin: '0 0 8px 0' }}>Paste the role ARN</h4>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
          Navigate to <strong>Outputs</strong> tab, locate the key named <strong>RoleArn</strong> and copy its value.
        </p>

        <div className="form-field">
          <label htmlFor="aws-role-arn">Role ARN</label>
          <input
            id="aws-role-arn"
            value={roleArn}
            onChange={(e) => setRoleArn(e.target.value)}
            placeholder="arn:aws:iam::123456789012:role/CadeSecurityAuditRole"
            autoComplete="off"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </div>

        {error && <p className="login-error">{error}</p>}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => transition('launch_stack')}>
            Back
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => { void captureArn(); }}
            disabled={captureMut.isPending}
          >
            Verify connection
          </button>
        </div>
      </div>
    );
  }

  if (state === 'launch_stack' && installUrl) {
    return (
      <div>
        <StepBar current={1} />
        <h4 style={{ margin: '0 0 8px 0' }}>Launch the CloudFormation stack</h4>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
          Click the button below to open AWS Console with the template and parameters pre-filled.
          The template is read-only — it creates one IAM role (<code>CadeSecurityAuditRole</code>)
          and one OIDC provider. No write actions are granted.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <a
            className="btn-primary"
            href={installUrl.cloudFormationUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            Launch stack in AWS Console
          </a>
          <CopyButton text={installUrl.cloudFormationUrl} label="Copy launch link" />
          <a
            className="btn-secondary"
            href={installUrl.templateUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            Download YAML
          </a>
        </div>

        <div style={{
          background: 'var(--color-bg-elevated)',
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          padding: 12,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8, fontWeight: 600 }}>
            What gets pre-filled in CloudFormation
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <InfoRow label="Stack name" value={installUrl.suggestedStackName} mono />
            <CopyButton text={installUrl.suggestedStackName} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <InfoRow label="CadeTenantId" value={installUrl.cadeTenantId} mono />
            <CopyButton text={installUrl.cadeTenantId} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <InfoRow label="TenantExternalId" value={installUrl.externalId} mono />
            <CopyButton text={installUrl.externalId} />
          </div>
          <InfoRow label="Template" value={installUrl.templateFileName} mono />
        </div>

        <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          In AWS Console: tick the IAM capabilities checkbox and click <strong>Create stack</strong>.
          Wait for status <code>CREATE_COMPLETE</code> (~30 seconds), copy the <code>RoleArn</code>{' '}
          from the Outputs tab, then continue below.
        </p>

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => transition('tier_selection')}>
            Back
          </button>
          <button type="button" className="btn-primary" onClick={() => transition('awaiting_role_arn')}>
            Stack created — continue
          </button>
        </div>
      </div>
    );
  }

  // Default: tier_selection.
  return (
    <div>
      <StepBar current={stepIndexForState('tier_selection')} />
      <h4 style={{ margin: '0 0 8px 0' }}>Choose a permission tier</h4>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
        All tiers are read-only. You can change this later by re-running the install.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {TIER_OPTIONS.filter((opt) => !opt.advanced).map((opt) => (
          <TierCard
            key={opt.id}
            opt={opt}
            selected={tier === opt.id}
            onSelect={() => setTier(opt.id)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: 13, padding: 0, marginBottom: 12 }}
      >
        {showAdvanced ? '▼' : '▶'} Advanced settings
      </button>

      {showAdvanced && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {TIER_OPTIONS.filter((opt) => opt.advanced).map((opt) => (
            <TierCard
              key={opt.id}
              opt={opt}
              selected={tier === opt.id}
              onSelect={() => setTier(opt.id)}
            />
          ))}
        </div>
      )}

      {tier !== 'full-read' && (
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 0, marginBottom: 12 }}>
          Selected tier: {TIER_OPTIONS.find((opt) => opt.id === tier)?.label}
        </p>
      )}

      {error && <p className="login-error">{error}</p>}

      <div className="modal-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => { void launchStack(); }}
          disabled={generateMut.isPending}
        >
          {generateMut.isPending ? 'Generating link…' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
